import { useUploadProductImage } from "@kupikupi/api";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Mini-App variant of the web `useImageSearchPicker` hook.
 *
 * Wires:
 *  1. Hidden `<input type="file">` mounted on demand.
 *  2. FileReader → base64 (strips `data:image/png;base64,` prefix).
 *  3. `POST /product/image/upload` → `imageId`.
 *  4. `navigate({ to: '/catalog', search: { imageId, channel } })` — the
 *     CatalogPage reads `imageId` and switches to image-similarity search.
 *
 * Channel default `1688` because TAOBAO image upload errors with 500 on the
 * backend (`openTaoBao image upload error`). 1688 succeeds.
 */
export function useImageSearchPicker(channel: "taobao" | "1688" = "1688") {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const upload = useUploadProductImage();

  useEffect(() => {
    const el = document.createElement("input");
    el.type = "file";
    el.accept = "image/*";
    el.style.display = "none";
    document.body.appendChild(el);
    inputRef.current = el;
    return () => {
      el.remove();
      inputRef.current = null;
    };
  }, []);

  const open = useCallback(() => {
    const el = inputRef.current;
    if (!el || isUploading) return;
    setError(null);

    el.onchange = async () => {
      const file = el.files?.[0];
      el.value = "";
      if (!file) return;
      try {
        setIsUploading(true);
        const reader = new FileReader();
        const base64: string = await new Promise((resolve, reject) => {
          reader.onload = () => {
            const result = reader.result;
            if (typeof result !== "string") {
              reject(new Error("FileReader returned non-string"));
              return;
            }
            const idx = result.indexOf(",");
            resolve(idx >= 0 ? result.slice(idx + 1) : result);
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(file);
        });

        const imageId = await upload.mutateAsync({
          imageBase64: base64,
          channel,
        });

        navigate({
          to: "/catalog" as never,
          search: { imageId, channel } as never,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : "Image upload failed");
      } finally {
        setIsUploading(false);
      }
    };
    el.click();
  }, [channel, isUploading, navigate, upload]);

  return { open, isUploading, error };
}
